import { create as createProto } from '@bufbuild/protobuf';
import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createEnvelope } from '@/lib/protobuf/codec';
import type { MessageEnvelope } from '@/lib/protobuf/gen/envelope/envelope_pb';
import type {
  Profile,
  Template,
} from '@/lib/protobuf/gen/prompt/prompt_pb';
import {
  ConversationMessageSchema,
  GetTemplatesRequestSchema,
  ListProfilesRequestSchema,
  MessageRole,
  QuestionType,
  SendPromptBuilderMessageRequestSchema,
} from '@/lib/protobuf/gen/prompt/prompt_pb';
import { wsManager } from '@/lib/websocket';

type PromptBuilderState = 'idle' | 'loading' | 'streaming' | 'error' | 'critical_failure';

interface PromptBuilderMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface PromptBuilderStoreState {
  state: PromptBuilderState;
  messages: PromptBuilderMessage[];
  currentStreamingMessage: string;
  isStreaming: boolean;
  turnCount: number;
  suggestedPrompt: string;
  suggestedProfile: string;
  profileExplanation: string;
  suggestedLimits: { maxNodes: number; maxDepth: number; maxChildren: number } | null;
  selectedTemplateId: string | null;
  suggestionVersion: number;
  suggestedSections: Array<{ title: string; content: string }>;
  suggestedExclusions: string[];
  structuredQuestions: Array<{
    question: string;
    type: 'free_text' | 'single_choice' | 'multi_select';
    options: Array<{ label: string; value: string }>;
  }>;
  maxTurns: 20;
  availableProfiles: Profile[];
  templates: Template[];

  actions: {
    sendMessage: (content: string, profileId?: string, templateId?: string) => void;
    reset: () => void;
    applyTemplate: (templateId: string) => void;
    loadProfiles: () => void;
    loadTemplates: (category?: string) => void;
    handlePromptBuilderMessageEvent: (envelope: MessageEnvelope) => void;
    handleListProfilesResponse: (envelope: MessageEnvelope) => void;
    handleGetTemplatesResponse: (envelope: MessageEnvelope) => void;
  };
}

const PERSISTENCE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

let promptBuilderTimer: ReturnType<typeof setTimeout> | null = null;

export const usePromptBuilderStore = create<PromptBuilderStoreState>()(
  devtools(
    subscribeWithSelector(
      persist(
        immer((set, get) => ({
          state: 'idle' as PromptBuilderState,
          messages: [] as PromptBuilderMessage[],
          currentStreamingMessage: '',
          isStreaming: false,
          turnCount: 0,
          suggestedPrompt: '',
          suggestedProfile: '',
          profileExplanation: '',
          suggestedLimits: null as { maxNodes: number; maxDepth: number; maxChildren: number } | null,
          selectedTemplateId: null as string | null,
          suggestionVersion: 0,
          suggestedSections: [] as Array<{ title: string; content: string }>,
          suggestedExclusions: [] as string[],
          structuredQuestions: [] as Array<{
            question: string;
            type: 'free_text' | 'single_choice' | 'multi_select';
            options: Array<{ label: string; value: string }>;
          }>,
          maxTurns: 20 as const,
          availableProfiles: [] as Profile[],
          templates: [] as Template[],

          actions: {
            sendMessage: (content: string, profileId?: string, templateId?: string) => {
              const { turnCount, maxTurns, messages, selectedTemplateId } = get();

              if (turnCount >= maxTurns) {
                set((state) => {
                  state.state = 'error';
                });
                return;
              }

              // Build conversation history from existing messages (before adding new one)
              const conversationHistory = messages.map((msg) => createProto(ConversationMessageSchema, {
                role: msg.role === 'user' ? MessageRole.USER : MessageRole.ASSISTANT,
                content: msg.content,
              }));

              // Add user message to store (only if content is non-empty — template triggers send with empty message)
              if (content) {
                set((state) => {
                  state.messages.push({
                    role: 'user',
                    content,
                    timestamp: Date.now(),
                  });
                  state.state = 'loading';
                  state.turnCount += 1;
                  state.currentStreamingMessage = '';
                  state.isStreaming = false;
                });
              } else {
                set((state) => {
                  state.state = 'loading';
                  state.currentStreamingMessage = '';
                  state.isStreaming = false;
                });
              }

              const req = createProto(SendPromptBuilderMessageRequestSchema, {
                profileId: profileId ?? '',
                message: content,
                conversationHistory,
                templateId: templateId ?? selectedTemplateId ?? '',
              });
              wsManager.sendEnvelope(
                createEnvelope({ case: 'sendPromptBuilderMessageRequest', value: req }),
              );

              if (promptBuilderTimer) clearTimeout(promptBuilderTimer);
              promptBuilderTimer = setTimeout(() => {
                promptBuilderTimer = null;
                const s = get().state;
                if (s === 'loading' || s === 'streaming') {
                  set((state) => {
                    state.state = 'error';
                    state.isStreaming = false;
                    state.currentStreamingMessage = '';
                  });
                }
              }, 30_000);
            },

            reset: () => {
              set((state) => {
                state.state = 'idle';
                state.messages = [];
                state.currentStreamingMessage = '';
                state.isStreaming = false;
                state.turnCount = 0;
                state.suggestedPrompt = '';
                state.suggestedProfile = '';
                state.profileExplanation = '';
                state.suggestedLimits = null;
                state.suggestedSections = [];
                state.suggestedExclusions = [];
                state.selectedTemplateId = null;
                state.suggestionVersion = 0;
                state.structuredQuestions = [];
              });
            },

            applyTemplate: (templateId: string) => {
              set((state) => {
                state.state = 'idle';
                state.messages = [];
                state.currentStreamingMessage = '';
                state.isStreaming = false;
                state.turnCount = 0;
                state.suggestedPrompt = '';
                state.suggestedProfile = '';
                state.profileExplanation = '';
                state.suggestedLimits = null;
                state.selectedTemplateId = templateId;
                state.suggestionVersion = 0;
                state.suggestedSections = [];
                state.suggestedExclusions = [];
                state.structuredQuestions = [];
              });
              // No message sent — user types first
            },

            loadProfiles: () => {
              const req = createProto(ListProfilesRequestSchema, {});
              wsManager.sendEnvelope(
                createEnvelope({ case: 'listProfilesRequest', value: req }),
              );
            },

            loadTemplates: (category?: string) => {
              const req = createProto(GetTemplatesRequestSchema, {
                category: category ?? '',
              });
              wsManager.sendEnvelope(
                createEnvelope({ case: 'getTemplatesRequest', value: req }),
              );
            },

            handlePromptBuilderMessageEvent: (envelope: MessageEnvelope) => {
              if (envelope.payload.case !== 'promptBuilderMessageEvent') return;
              const event = envelope.payload.value;

              set((state) => {
                if (event.isComplete) {
                  if (promptBuilderTimer) {
                    clearTimeout(promptBuilderTimer);
                    promptBuilderTimer = null;
                  }
                  // Finalize: add the accumulated message to the messages array
                  // When the Done event carries cleaned content (questions stripped),
                  // use it as the authoritative message instead of the raw accumulated stream.
                  const hasQuestions = (event.questions ?? []).length > 0;
                  const finalContent = hasQuestions && event.message
                    ? event.message
                    : state.currentStreamingMessage + event.message;

                  // Silent template selection returns empty content — don't add empty message
                  if (finalContent) {
                    state.messages.push({
                      role: 'assistant',
                      content: finalContent,
                      timestamp: Date.now(),
                    });
                  }
                  state.currentStreamingMessage = '';
                  state.isStreaming = false;
                  state.state = 'idle';

                  // Handle suggestion if present
                  if (event.suggestion) {
                    state.suggestedPrompt = event.suggestion.suggestedPrompt;
                    state.suggestedProfile = event.suggestion.suggestedProfile;
                    state.profileExplanation = event.suggestion.profileExplanation;
                    if (event.suggestion.suggestedLimits) {
                      state.suggestedLimits = {
                        maxNodes: event.suggestion.suggestedLimits.maxNodes,
                        maxDepth: event.suggestion.suggestedLimits.maxDepth,
                        maxChildren: event.suggestion.suggestedLimits.maxChildren,
                      };
                    }
                    state.suggestedSections = (event.suggestion.sections ?? []).map(s => ({
                      title: s.title,
                      content: s.content,
                    }));
                    state.suggestedExclusions = event.suggestion.exclusions ?? [];
                    state.suggestionVersion += 1;
                  }

                  // Map structured questions from the event
                  state.structuredQuestions = (event.questions ?? []).map(q => ({
                    question: q.question,
                    type: q.type === QuestionType.SINGLE_CHOICE ? 'single_choice' as const : q.type === QuestionType.MULTI_SELECT ? 'multi_select' as const : 'free_text' as const,
                    options: (q.options ?? []).map(o => ({ label: o.label, value: o.value })),
                  }));
                } else {
                  // Accumulate streaming chunk
                  state.currentStreamingMessage += event.message;
                  state.isStreaming = true;
                  state.state = 'streaming';
                }
              });
            },

            handleListProfilesResponse: (envelope: MessageEnvelope) => {
              if (envelope.payload.case !== 'listProfilesResponse') return;
              const { profiles } = envelope.payload.value;

              set((state) => {
                state.availableProfiles = profiles;
              });
            },

            handleGetTemplatesResponse: (envelope: MessageEnvelope) => {
              if (envelope.payload.case !== 'getTemplatesResponse') return;
              const { templates } = envelope.payload.value;

              set((state) => {
                state.templates = templates;
              });
            },
          },
        })),
        {
          // SEC-34: lvdr_session_ prefix so hardLogout auto-nukes this
          // account-tied prompt-builder conversation on logout/lock.
          name: 'lvdr_session_prompt_builder_state',
          partialize: (state) => ({
            messages: state.messages,
            turnCount: state.turnCount,
            suggestedPrompt: state.suggestedPrompt,
            suggestedProfile: state.suggestedProfile,
            profileExplanation: state.profileExplanation,
            suggestedLimits: state.suggestedLimits,
            selectedTemplateId: state.selectedTemplateId,
            suggestedSections: state.suggestedSections,
            suggestedExclusions: state.suggestedExclusions,
            _persistedAt: Date.now(),
          }),
          merge: (persisted, current) => {
            const data = persisted as Record<string, unknown> | null;
            if (!data) return current;

            // Check 24h expiry
            const persistedAt = data._persistedAt as number | undefined;
            if (persistedAt && Date.now() - persistedAt > PERSISTENCE_EXPIRY_MS) {
              return current;
            }

            return {
              ...current,
              messages: (data.messages as PromptBuilderMessage[]) ?? current.messages,
              turnCount: (data.turnCount as number) ?? current.turnCount,
              suggestedPrompt: (data.suggestedPrompt as string) ?? current.suggestedPrompt,
              suggestedProfile: (data.suggestedProfile as string) ?? current.suggestedProfile,
              profileExplanation:
                (data.profileExplanation as string) ?? current.profileExplanation,
              suggestedLimits: (data.suggestedLimits as { maxNodes: number; maxDepth: number; maxChildren: number } | null) ?? current.suggestedLimits,
              selectedTemplateId: (data.selectedTemplateId as string | null) ?? current.selectedTemplateId,
              suggestedSections: (data.suggestedSections as Array<{ title: string; content: string }>) ?? current.suggestedSections,
              suggestedExclusions: (data.suggestedExclusions as string[]) ?? current.suggestedExclusions,
            };
          },
        },
      ),
    ),
    { name: 'PromptBuilderStore' },
  ),
);

/**
 * Register WebSocket handlers for prompt builder messages.
 * Call once on app initialization. Returns a cleanup function.
 */
export function registerPromptBuilderHandlers(): () => void {
  const { actions } = usePromptBuilderStore.getState();

  const unsubscribers = [
    wsManager.onMessage('promptBuilderMessageEvent', actions.handlePromptBuilderMessageEvent),
    wsManager.onMessage('listProfilesResponse', actions.handleListProfilesResponse),
    wsManager.onMessage('getTemplatesResponse', actions.handleGetTemplatesResponse),
    // Catch error envelopes so the store doesn't stay stuck in 'loading'.
    // Uses onAnyMessage (wildcard dispatch) instead of onMessage('*') which
    // only matches the literal key '*' and never fires.
    wsManager.onAnyMessage((envelope) => {
      if (envelope.error && usePromptBuilderStore.getState().state === 'loading') {
        if (promptBuilderTimer) {
          clearTimeout(promptBuilderTimer);
          promptBuilderTimer = null;
        }
        usePromptBuilderStore.setState((state) => {
          state.state = 'error';
          // Remove the pending user message that will never get a response
          const lastMsg = state.messages[state.messages.length - 1];
          if (lastMsg?.role === 'user') {
            state.messages.pop();
            state.turnCount = Math.max(0, state.turnCount - 1);
          }
        });
      }
    }),
  ];

  return () => {
    for (const unsub of unsubscribers) {
      unsub();
    }
  };
}
